import { CaretRight } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import { ProxyLocationTag } from "common/components/Proxy";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import type { CustomProxy } from "common/types";

import MobileListTable from "../../MobileListTable";
import MobilePreview from "./MobilePreview";

import type { ColumnDef } from "@tanstack/react-table";

export default function MobileList({ proxies }: { proxies: CustomProxy[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProxyId, setSelectedProxyId] = useState<string>(null);

  function onRowClick(proxy: CustomProxy) {
    setSelectedProxyId(proxy.id);
    setIsOpen(true);
  }

  const selectedProxy = useMemo(
    () => proxies.find(({ id }) => id === selectedProxyId),
    [proxies, selectedProxyId],
  );

  const columns = useMemo(() => {
    const templateColumns: ColumnDef<CustomProxy>[] = [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row: { original } }) => (
          <span className="line-clamp-1 break-all text-sm-bold">
            {original.name}
          </span>
        ),
      },
    ];

    templateColumns.push({
      id: "location",
      accessorKey: "location",
      header: "Location",
      cell: ({ row: { original } }) => {
        const { location, valid: isValid } = original;

        return (
          <ProxyLocationTag
            location={
              location && {
                countryCode: location.countryISO,
                name: location.city || location.country,
              }
            }
            size="md"
            isValid={isValid}
          />
        );
      },
    });
    templateColumns.push({
      id: "actions",
      cell: () => (
        <Button intent="labelIcon" size="sm" variant="ghost-black">
          <CaretRight />
        </Button>
      ),
    });

    return templateColumns;
  }, []);

  return (
    <>
      <MobileListTable
        columns={columns}
        data={proxies}
        onRowClick={onRowClick}
        isLoading={false}
      />

      <RenderIf condition={!!selectedProxy}>
        <MobilePreview
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          proxy={selectedProxy}
        />
      </RenderIf>
    </>
  );
}
